import React, { useState, useRef, useEffect } from "react";
import Icon from "../common/Icon";
import "./EditableProjectTitle.css";

const EditableProjectTitle = ({ name, fallback, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(name || "");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraftName(name || "");
  }, [name]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const displayName = name && name.trim().length > 0 ? name : fallback;

  const handleStartEdit = () => {
    setDraftName(name || "");
    setErrorMessage(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftName(name || "");
    setErrorMessage(null);
    setIsEditing(false);
  };

  const handleDraftChange = (event) => {
    setDraftName(event.target.value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const handleSave = async () => {
    const trimmed = draftName.trim();
    const previous = (name || "").trim();
    if (trimmed === previous) {
      setIsEditing(false);
      return;
    }
    try {
      setIsSaving(true);
      setErrorMessage(null);
      await onRename(trimmed.length > 0 ? trimmed : null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error renaming project:", error);
      setErrorMessage("Erreur lors du renommage. Réessaye.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSave();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="editable-project-title editing">
        <div className="editable-project-title-row">
          <input
            ref={inputRef}
            type="text"
            className="editable-project-title-input"
            value={draftName}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            maxLength={255}
            disabled={isSaving}
            aria-label="Modifier le nom du projet"
            aria-invalid={errorMessage ? "true" : "false"}
          />
          <button
            type="button"
            className="editable-project-title-action save"
            onClick={handleSave}
            disabled={isSaving}
            aria-label="Enregistrer le nouveau nom"
          >
            <Icon name="Check" size={16} />
          </button>
          <button
            type="button"
            className="editable-project-title-action cancel"
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Annuler la modification"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
        {errorMessage && (
          <div className="editable-project-title-error" role="alert">
            {errorMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="editable-project-title">
      <h1 className="analysis-title">{displayName}</h1>
      <button
        type="button"
        className="editable-project-title-edit"
        onClick={handleStartEdit}
        aria-label="Renommer le projet"
        title="Renommer le projet"
      >
        <Icon name="Pencil" size={14} />
      </button>
    </div>
  );
};

export default EditableProjectTitle;
